import { Link } from 'react-router-dom';
import { Tags, Timer, Compass, Link as LinkIcon } from 'lucide-react';
import { LandingPage, LandingCTA } from './LandingShell';

const oldWay = [
  'Open app. See 47 overdue tasks. Feel shame.',
  'Spend 20 minutes dragging tasks between projects.',
  'Manually set priorities on 12 items. Get 8 wrong.',
  "Give up and do whatever's loudest.",
  'Repeat Tuesday.',
];

const dannyWay = [
  'Open app. Danny already classified everything overnight.',
  'Tasks are tagged, estimated, and prioritized.',
  'Danny already answered "what matters today."',
  'Do the work.',
];

const features = [
  {
    icon: Tags,
    title: 'Classification',
    description:
      'Danny reads your tasks and understands what they are. Project, type, complexity. Automatic.',
  },
  {
    icon: Timer,
    title: 'Estimation',
    description:
      'How long will this take? Danny answers that. You were going to guess 30 minutes and be wrong anyway.',
  },
  {
    icon: Compass,
    title: 'What should I do next?',
    description:
      'Not calendar Tetris. Not earliest-deadline-first. Actual context-aware prioritization.',
  },
  {
    icon: LinkIcon,
    title: 'Your existing tools still work',
    description:
      "Danny connects to Todoist and makes it smarter. You don't have to start over.",
  },
];

export function LandingPageB() {
  return (
    <LandingPage>
      {/* Hero */}
      <section className="max-w-3xl mx-auto px-6 pt-24 pb-20 text-center">
        <span className="label-mono inline-block text-primary-500 border border-primary-500/40 rounded-full px-4 py-1 mb-8 text-xs">
          there's a better way
        </span>

        <h1 className="text-4xl sm:text-5xl font-bold text-text-primary-light dark:text-text-primary-dark tracking-tight leading-tight mb-6">
          Stop organizing.
          <br />
          <span className="text-primary-500">Start finishing.</span>
        </h1>

        <p className="text-lg text-text-secondary-light dark:text-text-secondary-dark max-w-2xl mx-auto mb-10 leading-relaxed">
          Your old app stores your tasks. Danny understands them. $2/month.
        </p>

        <div className="flex items-center justify-center gap-4">
          <Link
            to="/register"
            className="bg-primary-500 hover:bg-primary-400 text-surface-base-dark font-semibold px-6 py-3 rounded-xl transition-colors"
          >
            Try Danny
          </Link>
          <Link
            to="/login"
            className="border border-border-subtle-light dark:border-border-subtle-dark text-text-primary-light dark:text-text-primary-dark hover:border-border-hover-light dark:hover:border-border-hover-dark font-semibold px-6 py-3 rounded-xl transition-colors"
          >
            Sign in
          </Link>
        </div>
      </section>

      {/* Side-by-Side Comparison */}
      <section className="max-w-5xl mx-auto px-6 pb-24">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Old way */}
          <div className="rounded-2xl bg-surface-raised-dark border border-border-subtle-dark p-8">
            <h3 className="text-sm label-mono text-text-tertiary-dark mb-6">
              Monday morning. Your current app.
            </h3>
            <ol className="space-y-4">
              {oldWay.map((step, i) => (
                <li key={i} className="flex gap-3">
                  <span className="font-mono text-sm text-text-tertiary-dark mt-0.5 shrink-0">
                    {i + 1}.
                  </span>
                  <span className="text-text-secondary-dark leading-relaxed">
                    {step}
                  </span>
                </li>
              ))}
            </ol>
          </div>

          {/* Danny way */}
          <div className="rounded-2xl bg-surface-raised-dark border border-primary-500/30 p-8 ring-1 ring-primary-500/10">
            <h3 className="text-sm label-mono text-primary-500 mb-6">
              Monday morning. Danny.
            </h3>
            <ol className="space-y-4">
              {dannyWay.map((step, i) => (
                <li key={i} className="flex gap-3">
                  <span className="font-mono text-sm text-primary-500/70 mt-0.5 shrink-0">
                    {i + 1}.
                  </span>
                  <span className="text-text-primary-dark leading-relaxed">
                    {step}
                  </span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Why is this happening */}
      <section className="max-w-3xl mx-auto px-6 pb-24">
        <h2 className="text-3xl font-bold text-text-primary-light dark:text-text-primary-dark tracking-tight mb-6">
          Your task app is a spreadsheet with a logo.
        </h2>
        <p className="text-text-secondary-light dark:text-text-secondary-dark leading-relaxed mb-4">
          Todoist, Asana, Notion. They store text and let YOU do all the
          thinking. When they added AI, they wrapped ChatGPT in a button and
          charged you more. Your task app's AI is a GPT wrapper in a trench
          coat.
        </p>
        <p className="text-text-secondary-light dark:text-text-secondary-dark leading-relaxed">
          Danny uses the same AI models as the $34/month apps. We just built
          around them instead of bolting them on. And we charge $2/month
          because we're not subsidizing a sales team.
        </p>
      </section>

      {/* Features */}
      <section className="max-w-5xl mx-auto px-6 pb-24">
        <div className="grid sm:grid-cols-2 gap-6">
          {features.map((feature) => (
            <div
              key={feature.title}
              className="rounded-2xl bg-surface-raised-light dark:bg-surface-raised-dark border border-border-subtle-light dark:border-border-subtle-dark p-6"
            >
              <feature.icon className="w-5 h-5 text-primary-500 mb-3" />
              <h3 className="font-semibold text-text-primary-light dark:text-text-primary-dark mb-2">
                {feature.title}
              </h3>
              <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <LandingCTA
        headline="$2/month to stop being your own task manager."
        subtitle="Your old task app won't miss you."
      />
    </LandingPage>
  );
}
